import type { Move } from '../../../schemas/move';
import { warlockArchetypeMoves } from './archetypes';
import {
  warlockInitialMoves,
  warlockStartingAutoMoves,
  warlockStartingChoiceMoves,
} from './initial';
import { warlockAdvanced2to5Moves } from './advanced-2-5';
import { warlockAdvanced6to10Moves } from './advanced-6-10';

export {
  warlockArchetypeMoves,
  warlockInitialMoves,
  warlockStartingAutoMoves,
  warlockStartingChoiceMoves,
  warlockAdvanced2to5Moves,
  warlockAdvanced6to10Moves,
};

/**
 * Catalogo completo delle mosse del Warlock (manuale V1, sezione *Il Warlock*,
 * rr. 5768-6177).
 *
 * Aggregato di:
 *  - 5 archetipi (`class_special`)
 *  - 3 mosse iniziali automatiche + 3 mosse iniziali di scelta (`starting`,
 *    `pickCount: 1` sul gruppo dei 3 Vincoli)
 *  - 16 mosse avanzate L2-5 (`advanced_2_5`)
 *  - 14 mosse avanzate L6-10 (`advanced_6_10`)
 *
 * Totale: **41 mosse**.
 *
 * **Pattern stress-testati**:
 *
 *  - **5ª e ultima casteggiante DWR**, casteggiante condizionata a *Vincolo del Tomo* (D2).
 *    Pattern coerente con Druido (*Divinità della Natura*) e Paladino (*Ordine Sacro*):
 *    la mossa di scelta È il trigger semantico per `getActiveSpellLists`. *Mana* e *Lanciare
 *    Incantesimi del Patrono* sono regole description-only dentro il testo di *Vincolo del
 *    Tomo*, non Move separate nel catalogo.
 *
 *  - **`prerequisites.anyOf` cross-class verso Paladino**: 12 mosse hanno «Richiede:
 *    *Contratto* o *Ordine Sovrannaturale*» (i 3 Vincoli iniziali + 5 L2-5 + 4 L6-10).
 *    `anyOf: ['warlock.contratto', 'paladino.ordine-sovrannaturale']`.
 *
 *  - **`prerequisites.anyOf` cross-class verso Guerriero**: *L'Anima dell'Acciaio* e *Lama
 *    Ultraterrena* hanno «Richiede: *Vincolo della Lama* o *Arma Prescelta*». Forward-ref
 *    simmetrico ai 3 cross-ref del Guerriero verso `warlock.vincolo-della-lama` (Cimelio,
 *    Arma Migliorata, Fabbro). Tutti consolidabili ora.
 *
 *  - **FI-8 stub description-only** (D6): 5 mosse legate a *Braccio Destro* (Servitore
 *    Competente, Sosia, Un Giusto Sovrano, Maestria del Servitore) sono description-only.
 *    Refactor unificato Ranghi 1-3 nella PR FI-8 post-12 classi. *Loggia* (req *Setta*) è
 *    un'organizzazione, non un gregario combattente — strutturata normalmente.
 *
 *  - **Catene di prerequisiti L2-5 → L6-10** (5 catene): Braccio Destro → Un Giusto Sovrano;
 *    Servitore Competente → Maestria del Servitore; Deflagrazione Potente → Deflagrazione
 *    Dirompente; Lama Ultraterrena → Stormbringer; Tomo del Sapere → Rivelazioni Proibite.
 *    Inoltre: Setta → Loggia; Altri Vincoli → Completamente Vincolato.
 *
 *  - **Mosse multiclasse**: *Le Vie del Patrono...* (L2-5: Paladino/Ladro/Bardo) e
 *    *... Sono Infinite* (L6-10: Paladino/Mago/Guerriero). Description-only (FI-17). Gli
 *    archetipi Elfo (Mago) e Mezz'orco (Guerriero) estendono il pool runtime
 *    description-only.
 *
 *  - **Niente `MoveSchema.replaces`** strutturali (manuale coerente).
 *
 *  - **D5 — Archetipo Warlock-Nano (immunità veleni)**: description-only nel testo di
 *    *Contratto* + nell'archetipo Nano. Niente `extraTags` schema.
 */
export const warlockMoves: readonly Move[] = [
  ...warlockArchetypeMoves,
  ...warlockInitialMoves,
  ...warlockAdvanced2to5Moves,
  ...warlockAdvanced6to10Moves,
];
